import ArrowLeft from '@assets/icons/arrow-left.svg';
import DeleteIcon from '@assets/icons/delete.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { APP_MODAL_ROUTES, PAYMENT_ROUTES } from '@/navigators/routes';
import { AppModalStackParams, PaymentsStackParams } from '@/navigators/types';
import { CompositeScreenProps, useNavigation } from '@react-navigation/native';
import { ModalContainer } from '@/components/modal-container';
import { PadPaymentForm, PadPaymentFormView } from './pad-payment-form';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { StyleSheet, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';

type Props = CompositeScreenProps<
  StackScreenProps<AppModalStackParams, APP_MODAL_ROUTES.CollectPADConsent>,
  StackScreenProps<PaymentsStackParams, PAYMENT_ROUTES.Base>
>;

const getStepInfo = (view: PadPaymentFormView) => {
  const totalSteps = 2;
  switch (view) {
    case PadPaymentFormView.ContactInformation:
      return { currentStep: 1, totalSteps };
    case PadPaymentFormView.Agreement:
    case PadPaymentFormView.AdditionalTerms:
      return { currentStep: 2, totalSteps };
    default:
      return { currentStep: 1, totalSteps };
  }
};

export const CollectPADConsentScreen: React.FC<Props> = ({ route }) => {
  const { onAfterPADConsent } = route.params;
  const navigation = useNavigation<StackNavigationProp<PaymentsStackParams>>();
  const { bottom } = useSafeAreaInsets();
  const theme = useTheme();

  const [currentView, setCurrentView] = useState(
    PadPaymentFormView.ContactInformation,
  );

  const stepInfo = getStepInfo(currentView);

  const handleBackPress = () => {
    switch (currentView) {
      case PadPaymentFormView.Agreement:
        setCurrentView(PadPaymentFormView.ContactInformation);
        break;
      case PadPaymentFormView.AdditionalTerms:
        setCurrentView(PadPaymentFormView.Agreement);
        break;
      default:
        navigation.goBack();
    }
  };

  const isAdditionalTermsView =
    currentView === PadPaymentFormView.AdditionalTerms;
  const isContactInfoView =
    currentView === PadPaymentFormView.ContactInformation;

  return (
    <ModalContainer
      navigationHeaderProps={{
        actions: isAdditionalTermsView
          ? []
          : [
              {
                component: (
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="label-md"
                  >
                    {i18n.t('step_x_of_y', {
                      current: stepInfo.currentStep,
                      total: stepInfo.totalSteps,
                    })}
                  </Typography>
                ),
              },
            ],
        BackButtonIcon: isContactInfoView ? DeleteIcon : ArrowLeft,
        backButtonTestID: isContactInfoView
          ? 'pad-action-sheet-close-button'
          : 'pad-action-sheet-back-button',
        centerTitle: true,
        onBackPress: handleBackPress,
        showBackButton: true,
        title: isAdditionalTermsView ? undefined : i18n.t('pad.agreement'),
      }}
    >
      <View style={[styles.container, { paddingBottom: bottom }]}>
        <PadPaymentForm
          currentView={currentView}
          isCollectingConsent
          onChangeView={setCurrentView}
          onClose={() => {
            // goBack must be called here first to dismiss the modal
            // before any additional navigation can occur
            navigation.goBack();
            onAfterPADConsent();
          }}
        />
      </View>
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 12,
  },
});
